import {useSharedFilter} from '../../shared/navigation/useSharedReport';
import {readSharedReport} from '../../shared/navigation/useSharedReport';
import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { HeaderToggleButton } from "../../shared/ui/overlay/HeaderToggleButton";
import { LineChartWithToggles } from "../../shared/ui/chart/LineChartWithToggles";
import FederalOverview from "./FederalOverview";
import { GeneralResults } from "./GeneralResults";
import { DistrictMap } from "./StateDistrictMap";

let federalRequest;
function loadFederalResults() {
  if (!federalRequest) {
    federalRequest = getDoc(doc(db, "ELECTION_RESULTS", "historical-federal"))
      .then(snapshot => {
        if (!snapshot.exists()) throw new Error("Federal results are not available yet.");
        return snapshot.data();
      }).catch(error => { federalRequest = null; throw error; });
  }
  return federalRequest;
}

export function buildFederalCharts(race, fromYear, throughYear) {
  const years = Object.keys(race.years).map(Number).filter(year => year >= fromYear && year <= throughYear).sort((a,b) => a-b);
  const parties = [{ key: "democrat", label: "Democrat", color: "#2563eb" }, { key: "republican", label: "Republican", color: "#dc2626" }];
  return {
    labels: years.map(String),
    counts: parties.map(party => ({ ...party, valueType: "count", defaultVisible: true, data: years.map(year => race.years[year][party.key]) })),
    shares: parties.map(party => ({ ...party, valueType: "percent", defaultVisible: true, data: years.map(year => {
      const row = race.years[year], total = row.democrat + row.republican;
      return total ? row[party.key] / total * 100 : null;
    }) })),
  };
}

function RaceCharts({ id, title, race, fromYear, throughYear }) {
  const [open, setOpen] = useState(() =>
    window.location.hash === `#federal-${id}-heading` ||
    (id === 'president' && Object.keys(readSharedReport('federal-president-votes')).length > 0)
  );
  const charts = buildFederalCharts(race, fromYear, throughYear);
  return <section className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
    <HeaderToggleButton reportNavigation title={title} isOpen={open} onToggle={() => setOpen(value => !value)} controlsId={`federal-${id}`} ariaLabel={`Toggle ${title} charts`} />
    {open && <div id={`federal-${id}`} className="space-y-5 pt-4">
      {charts.labels.length ? <>
        <LineChartWithToggles shareId={id === "president" ? "federal-president-votes" : undefined} shareHeadingId={`federal-${id}-heading`} title={`${title} — Votes by Year`} labels={charts.labels} series={charts.counts} initialChartType="bar" xAxisTitle="Election year" yAxisTitle="Votes in Glastonbury" />
        <LineChartWithToggles title={`${title} — Two-Party Vote Share`} labels={charts.labels} series={charts.shares} xAxisTitle="Election year" yAxisTitle="Two-party vote share (%)" yAxisTickFormatter={value => `${value}%`} tooltipValueFormatter={value => `${value.toFixed(1)}%`} />
      </> : <p className="p-4 text-sm text-gray-600">No published results for this race in the selected years.</p>}
    </div>}
  </section>;
}

const RACES = [
  ['president', 'President'], ['senate', 'U.S. Senate'],
  ['district-1', 'U.S. House — District 1'], ['district-2', 'U.S. House — District 2'],
];

export function FederalResults() {
  const [data, setData] = useState(null), [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [fromYear, setFromYear] = useSharedFilter("FederalResults.jsx:FederalResults:fromYear", null), [throughYear, setThroughYear] = useSharedFilter("FederalResults.jsx:FederalResults:throughYear", null);
  useEffect(() => {
    let active = true;
    setError('');
    loadFederalResults().then(result => {
      if (!active) return;
      const years = Object.values(result.races).flatMap(race => Object.keys(race.years).map(Number));
      const params = new URLSearchParams(window.location.search);
      const from = Number(params.get('from')), through = Number(params.get('through'));
      const validRange = years.includes(from) && years.includes(through) && from <= through;
      setFromYear.initialize(validRange ? from : Math.min(...years)); setThroughYear.initialize(validRange ? through : Math.max(...years)); setData(result);
    }).catch(error => { if (active) setError(error.message); });
    return () => { active = false; };
  }, [attempt]);
  const updateYears = (from, through) => {
    setFromYear(from); setThroughYear(through);
    const url = new URL(window.location.href);
    url.searchParams.set('from', from); url.searchParams.set('through', through);
    window.history.replaceState(window.history.state, '', url);
  };
  if (error) return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4">Unable to load federal results: {error} <button type="button" onClick={() => setAttempt(value => value+1)} className="ml-2 underline">Try again</button></div>;
  if (!data) return <p role="status" className="p-4 text-gray-600">Loading federal election results…</p>;
  const years = [...new Set(Object.values(data.races).flatMap(race => Object.keys(race.years).map(Number)))].sort((a,b) => a-b);
  return <div className="space-y-5">
    <FederalOverview races={data.races}/>
    <DistrictMap mapId="1LxT3VVOZenDT3vGUOmX1JsLqhapRPrA" districtType="federal"/>
    <p className="text-sm text-gray-600">Glastonbury votes in federal elections. Vote shares compare Democrat and Republican votes only; other candidates are excluded. Missing elections are not shown as zero.</p>
    <div className="flex flex-wrap gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <label className="flex items-center gap-2 text-sm font-semibold">From year<select className="rounded border border-gray-300 bg-white px-3 py-2" value={fromYear} onChange={event => updateYears(Number(event.target.value), throughYear)}>{years.filter(year => year <= throughYear).map(year => <option key={year}>{year}</option>)}</select></label>
      <label className="flex items-center gap-2 text-sm font-semibold">Through year<select className="rounded border border-gray-300 bg-white px-3 py-2" value={throughYear} onChange={event => updateYears(fromYear, Number(event.target.value))}>{years.filter(year => year >= fromYear).map(year => <option key={year}>{year}</option>)}</select></label>
    </div>
    {RACES.filter(([id]) => data.races[id]).map(([id, title]) => <RaceCharts key={id} id={id} title={title} race={data.races[id]} fromYear={fromYear} throughYear={throughYear} />)}
    <GeneralResults report="federal" fromYear={fromYear} throughYear={throughYear} />
    <p className="text-xs text-gray-500">District boundaries changed during this period, so district counts may cover different portions of town. Source: <a className="underline" href="https://docs.google.com/spreadsheets/d/1fz5NEUg06HqNY3F1FOkhGBJ7KZtnYHY05b5JW5j-9pg/edit#gid=1129255278" target="_blank" rel="noreferrer">Historical Federal Results</a>.</p>
  </div>;
}
